import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import useGetFreeSlotSchedules from '../api/useGetFreeSlotSchedules';
import useRefreshWhenStale, { refetchDaysThrough } from '../../../hooks/useRefreshWhenStale';

const useBookingSchedules = (supplyId, providerId) => {
  const [fetchedDailySchedules, setFetchedDailySchedules] = useState([]);
  const [loadedAt, setLoadedAt] = useState(null);
  // Bumped by every reload and refresh, so a background refresh that finishes after a reload
  // has started knows its result is the older one and drops it.
  const generationRef = useRef(0);
  const {
    data: newDailySchedules,
    loading,
    getFreeSlotSchedules,
    fetchFreeSlotSchedules,
  } = useGetFreeSlotSchedules();

  const reloadSchedules = () => {
    generationRef.current += 1;
    setFetchedDailySchedules([]);
    setLoadedAt(Date.now());
    getFreeSlotSchedules(dayjs(), supplyId, providerId);
  };

  useEffect(() => {
    if (supplyId) {
      reloadSchedules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplyId, providerId]);

  useEffect(() => {
    if (newDailySchedules.length === 0) return;
    const firstNewDate = newDailySchedules[0]?.date;
    const fetchedDates = fetchedDailySchedules.map(ds => ds.date);
    if (fetchedDates.includes(firstNewDate)) return;
    setFetchedDailySchedules(prev => [...prev, ...newDailySchedules]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newDailySchedules]);

  const loadMoreSchedules = () => {
    const nextDay = dayjs(newDailySchedules[newDailySchedules.length - 1].date).add(1, 'day');
    getFreeSlotSchedules(nextDay, supplyId, providerId);
  };

  /**
   * Run when the free slots go stale — other guests keep booking while this page sits open.
   * The days already paged out to are fetched again off to the side and swapped in whole, so
   * the page the guest is looking at stays put instead of dropping back to a spinner and the
   * first page.
   */
  const refreshSchedules = async () => {
    if (!supplyId) return;

    const lastDate = fetchedDailySchedules[fetchedDailySchedules.length - 1]?.date;

    // Nothing loaded, or all of it already in the past: start over.
    if (!lastDate || lastDate < dayjs().format('YYYY-MM-DD')) {
      reloadSchedules();
      return;
    }

    const generation = ++generationRef.current;
    setLoadedAt(Date.now());

    const days = await refetchDaysThrough(
      (startDate) => fetchFreeSlotSchedules(startDate, supplyId, providerId),
      lastDate
    );
    if (!days || generation !== generationRef.current) return;

    // Days paged out to while the refresh was in flight are kept.
    setFetchedDailySchedules((prev) => [...days, ...prev.filter((day) => day.date > lastDate)]);
  };

  useRefreshWhenStale(refreshSchedules, loadedAt);

  return {
    fetchedDailySchedules,
    loading,
    loadMoreSchedules,
    reloadSchedules,
    refreshSchedules,
  };
};

export default useBookingSchedules;
